import { BusinessProduct } from "@1games-ru/region-shared/dist/backend/requests/business";
import { ProductValueType } from "@1games-ru/region-shared/dist/enums/business/business";
import { memo, useCallback } from "react";
import { useTranslation } from "react-i18next";
import clsx from "clsx";
import formatPrice from "@/core/helpers/formatPrice";
import { Clock2Icon, Lock3Icon } from "@/shared/icons";
import { Button, Icon } from "@/shared/ui";
import BusinessCardApply from "@/domains/business/components/BusinessCardApply";
import { BusinessPricesForServicessSlider } from "@/domains/business/components/pricesForServices/BusinessPricesForServicessSlider";

type Props = {
  src: string;
  item: BusinessProduct;
  setExtraCharge: (
    productId: number,
    percent: number,
    setIsAccept: (value: boolean) => void,
  ) => void;
  isUpdating: boolean;
  isLocked: boolean;
  isHour: boolean;
  remainingTime: string;
  selectedValue: number;
  min: number;
  max: number;
  step?: number;
  handleValueChange: (value: number) => void;
  isAccept: boolean;
  setIsAccept: (value: boolean) => void;
};

function BusinessPricesForServicessServiceCard({
  src,
  item,
  setExtraCharge,
  isUpdating,
  isLocked,
  isHour,
  remainingTime,
  selectedValue,
  min,
  max,
  step = 1,
  handleValueChange,
  isAccept,
  setIsAccept,
}: Props) {
  const { t } = useTranslation("business");

  const title = t(
    `business.pricesForServices.${
      item.valueType === ProductValueType.Price ? "extraChargeApply" : "comissionApply"
    }`,
  );
  const subTitle = t(
    `business.pricesForServices.${
      item.valueType === ProductValueType.Price
        ? "extraChargeNotBeAvailable"
        : "comissionNotBeAvailable"
    }`,
  );
  const notAvailableText = t(
    `business.pricesForServices.${
      item.valueType === ProductValueType.Price
        ? "extraChargeNotAvailable"
        : "comissionNotAvailable"
    }`,
  );

  const handleApply = useCallback(() => {
    setExtraCharge(item.id, selectedValue, setIsAccept);
  }, [item.id, selectedValue, setExtraCharge, setIsAccept]);

  return (
    <div className="relative pt-2 pb-3.5 bg-dark-800 rounded-2xl overflow-clip">
      <div
        className="relative flex justify-center items-center h-[6.875rem] mx-2 rounded-[0.4375rem] overflow-clip"
        style={{
          backgroundImage: "url(./svg/business/dots.svg)",
          backgroundRepeat: "no-repeat",
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <div
          className="size-full"
          style={{
            backgroundImage: `url(${src})`,
            backgroundSize: "contain",
            backgroundPosition: "center",
            backgroundRepeat: "no-repeat",
          }}
        />
        {isLocked && (
          <div className="absolute top-0 left-0 flex flex-col justify-center items-center size-full bg-dark-800/94">
            <div
              className={clsx(
                "absolute top-0 right-0 h-8 flex items-center px-1.5 bg-[#212121] bg-gradient-to-r from-[rgba(1,100,172,0.3)] to-[rgba(18,18,18,0.0)] rounded-bl-2xl",
                isHour ? "w-[5.25rem]" : "w-18",
              )}
            >
              <Clock2Icon className="size-3.5 mr-1 text-accent-solid" />
              <p className="text-12 font-bold">{remainingTime ?? "--:--"}</p>
            </div>
            <Lock3Icon className="mb-1.5 size-5.5 text-[#6C6C6C]" />
            <p className="max-w-[9.0625rem] text-14 font-medium text-white/50 text-center">
              {notAvailableText}
            </p>
          </div>
        )}
      </div>
      <div className="mt-2.5 px-3.5">
        <p className="mb-1 text-15 font-medium">{item.name}</p>
        <p className="mb-4 text-16 font-bold text-accent-solid">
          {formatPrice(item.value)} {item.valueType === ProductValueType.Price ? "₽" : "%"}
        </p>

        <BusinessPricesForServicessSlider
          value={selectedValue}
          min={min}
          max={max}
          step={step}
          onValueChange={handleValueChange}
          isLocked={isLocked}
        />

        <div className="flex gap-1.5 mt-2.5">
          <p className="flex justify-between items-center grow py-[0.6875rem] px-[0.8125rem] text-14 font-bold bg-accent-solid/15 rounded-xl">
            <span className="text-15 font-medium text-accent-solid">
              {item.valueType === ProductValueType.Price
                ? t("business.pricesForServices.extraCharge")
                : t("business.pricesForServices.comission")}
            </span>
            {selectedValue}%
          </p>
          <Button
            icon
            className="size-10.5 ml-auto"
            onClick={handleApply}
            disabled={isLocked || isUpdating}
          >
            <Icon name="checked" className="w-3" />
          </Button>
        </div>
      </div>
      <BusinessCardApply
        isShow={isAccept}
        setIsShow={setIsAccept}
        title={title}
        subTitle={subTitle}
        iconClassName="mb-1.5 w-8"
        titleClassName="text-16"
        subTitleClassName="text-14"
        timer={5}
      />
    </div>
  );
}

export default memo(BusinessPricesForServicessServiceCard);
